import React from "react";
import { TabPanel, TabView } from "primereact/tabview";
import DatasetChartConfig from "components/basic/dataset-chart-config/DatasetChartConfig";
import { T } from "services/lang/LanguageService";
import {
  DatasetScatterProps,
  ProductTemplateScatter,
  YAxisIdentifier,
  YAxisIdentifierSelectionType,
} from "../interfaces";
import DatasetsScatterChartDatasetsEditor from "./DatasetScatterChartDatasetsEditor";
import DatasetScatterChartColorEditor from "./DatasetScatterChartColorEditor";
import DatasetScatterChartImageEditor from "./DatasetScatterChartImageEditor";
import DatasetScatterChartRangesAndHighlightsEditor from "./DatasetScatterChartRangesAndHighlightsEditor";
import DatasetScatterChartTemplateOptions from "./DatasetScatterChartTemplateOptions";

interface DatasetScatterChartEditorProps {
  config: DatasetScatterProps;
  onConfigChange: (config: DatasetScatterProps) => void;
  bubbleChart?: boolean;
}

const DatasetScatterChartEditor = ({
  config,
  onConfigChange,
  bubbleChart,
}: DatasetScatterChartEditorProps) => {
  const handleChange = (
    field: keyof DatasetScatterProps,
    value: string | string[] | number | boolean | null | YAxisIdentifier[] | ProductTemplateScatter
  ) => {
    let updatedConfig = { ...config, [field]: value };

    if (value === null) delete updatedConfig[field];

    if (field === "colorAxisIdentifier" && value === null) {
      updatedConfig = { ...updatedConfig, discreteColorMode: false };
    }
    if (
      field === "yAxisIdentifierSelectionType" &&
      value === YAxisIdentifierSelectionType.MULTIPLE
    ) {
      delete updatedConfig.colorAxisIdentifier;
      delete updatedConfig.sizeAxisIdentifier;
      updatedConfig = {
        ...updatedConfig,
        discreteColorMode: false,
      };
    }
    onConfigChange(updatedConfig);
  };

  return (
    <TabView>
      {bubbleChart ? (
        <TabPanel header="Template options">
          <DatasetScatterChartTemplateOptions config={config} handleChange={handleChange} />
        </TabPanel>
      ) : (
        <TabPanel header={T("word.datasets")}>
          <DatasetsScatterChartDatasetsEditor config={config} handleChange={handleChange} />
        </TabPanel>
      )}
      <TabPanel header={!bubbleChart ? T("label.ranges-and-highlights") : T("word.ranges")}>
        <DatasetScatterChartRangesAndHighlightsEditor
          config={config}
          onConfigChange={onConfigChange}
          handleChange={handleChange}
          bubbleChart={bubbleChart}
        />
      </TabPanel>
      {!bubbleChart && (
        <TabPanel header={T("label.color-options")}>
          <DatasetScatterChartColorEditor config={config} onConfigChange={onConfigChange} />
        </TabPanel>
      )}
      <TabPanel header={T("word.image-options")}>
        <DatasetScatterChartImageEditor config={config} onConfigChange={onConfigChange} />
      </TabPanel>
      <TabPanel header={T("word.settings")}>
        <DatasetChartConfig
          config={config}
          onConfigChange={onConfigChange}
          bubbleChart={bubbleChart}
        />
      </TabPanel>
    </TabView>
  );
};

export default DatasetScatterChartEditor;
